import React, { useState, useEffect } from "react";
import { FuncionarioCreate, CargoFranquia } from "../types/Funcionario";
import { useAuth } from "../contexts/AuthContext";

interface FuncionarioFormProps {
  initialData?: Partial<FuncionarioCreate>;
  onSubmit: (data: FuncionarioCreate) => void;
  loading?: boolean;
  isEditing?: boolean;
}

const CARGOS_DISPONIVEIS: {
  value: CargoFranquia;
  label: string;
  description: string;
}[] = [
  {
    value: "GESTOR",
    label: "Gestor",
    description:
      "Pode gerenciar todos os aspectos da franquia (exceto adicionar outros gestores)",
  },
  {
    value: "FINANCEIRO",
    label: "Financeiro",
    description: "Responsável pela área financeira",
  },
  {
    value: "CADASTRAMENTO",
    label: "Cadastramento",
    description: "Responsável por cadastros e empreendimentos",
  },
  {
    value: "VENDEDOR",
    label: "Vendedor",
    description: "Responsável por vendas",
  },
];

export const FuncionarioForm: React.FC<FuncionarioFormProps> = ({
  initialData,
  onSubmit,
  loading = false,
  isEditing = false,
}) => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("ADMIN");

  const [formData, setFormData] = useState<FuncionarioCreate>({
    nome_completo: "",
    email: "",
    password: "",
    cpf: "",
    cargos: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCargoChange = (cargo: CargoFranquia, checked: boolean) => {
    setFormData((prev) => {
      let novosCargos = [...prev.cargos];

      if (checked) {
        // Adicionar cargo se não existir
        if (!novosCargos.includes(cargo)) {
          novosCargos.push(cargo);
        }

        if (cargo === "GESTOR") {
          // Lógica removida para não adicionar outros cargos automaticamente
        }
      } else {
        // Remover cargo
        novosCargos = novosCargos.filter((c) => c !== cargo);

        // Se removeu GESTOR, permitir que o usuário escolha quais outros cargos manter
        // (não remove automaticamente os outros)
      }

      return {
        ...prev,
        cargos: novosCargos,
      };
    });
  };

  const formatCPF = (value: string) => {
    // Remove tudo que não é dígito
    const digits = value.replace(/\D/g, "");

    // Aplica a máscara
    if (digits.length <= 11) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    return digits
      .slice(0, 11)
      .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setFormData((prev) => ({
      ...prev,
      cpf: formatted,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome_completo.trim()) {
      newErrors.nome_completo = "Nome completo é obrigatório";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email é obrigatório";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email inválido";
    }

    if (!isEditing && !formData.password.trim()) {
      newErrors.password = "Senha é obrigatória";
    } else if (!isEditing && formData.password.length < 6) {
      newErrors.password = "Senha deve ter pelo menos 6 caracteres";
    }

    if (!formData.cpf.trim()) {
      newErrors.cpf = "CPF é obrigatório";
    } else if (formData.cpf.replace(/\D/g, "").length !== 11) {
      newErrors.cpf = "CPF deve ter 11 dígitos";
    }

    if (formData.cargos.length === 0) {
      newErrors.cargos = "Selecione pelo menos um cargo";
    }

    // Verificar se usuário não-admin está tentando adicionar gestor
    if (!isAdmin && formData.cargos.includes("GESTOR")) {
      newErrors.cargos = "Apenas administradores podem adicionar gestores";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const isGestorSelecionado = formData.cargos.includes("GESTOR");

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="nome_completo"
            className="block text-sm font-medium text-[#29306a]"
          >
            Nome Completo *
          </label>
          <input
            type="text"
            id="nome_completo"
            name="nome_completo"
            value={formData.nome_completo}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#32bef0] focus:ring-[#32bef0] sm:text-sm ${
              errors.nome_completo ? "border-red-500" : ""
            }`}
            placeholder="Digite o nome completo"
          />
          {errors.nome_completo && (
            <p className="mt-1 text-sm text-red-600">{errors.nome_completo}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-[#29306a]"
          >
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#32bef0] focus:ring-[#32bef0] sm:text-sm ${
              errors.email ? "border-red-500" : ""
            }`}
            placeholder="Digite o email"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="cpf"
            className="block text-sm font-medium text-[#29306a]"
          >
            CPF *
          </label>
          <input
            type="text"
            id="cpf"
            name="cpf"
            value={formData.cpf}
            onChange={handleCPFChange}
            maxLength={14}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#32bef0] focus:ring-[#32bef0] sm:text-sm ${
              errors.cpf ? "border-red-500" : ""
            }`}
            placeholder="000.000.000-00"
          />
          {errors.cpf && (
            <p className="mt-1 text-sm text-red-600">{errors.cpf}</p>
          )}
        </div>

        {!isEditing && (
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-[#29306a]"
            >
              Senha *
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#32bef0] focus:ring-[#32bef0] sm:text-sm ${
                errors.password ? "border-red-500" : ""
              }`}
              placeholder="Digite a senha"
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password}</p>
            )}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-[#29306a] mb-3">
          Cargos *
        </label>
        <div className="space-y-3">
          {CARGOS_DISPONIVEIS.map((cargo) => {
            const isChecked = formData.cargos.includes(cargo.value);
            const isDisabled =
              (cargo.value === "GESTOR" && !isAdmin) || // Gestor só para admin
              (cargo.value !== "GESTOR" && isGestorSelecionado); // Outros cargos ficam "bloqueados" quando gestor está selecionado

            return (
              <div key={cargo.value} className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id={cargo.value}
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) =>
                      handleCargoChange(cargo.value, e.target.checked)
                    }
                    disabled={isDisabled}
                    className="focus:ring-[#32bef0] h-4 w-4 text-[#32bef0] border-gray-300 rounded disabled:opacity-50"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label
                    htmlFor={cargo.value}
                    className={`font-medium ${
                      isDisabled ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    {cargo.label}
                    {cargo.value === "GESTOR" && !isAdmin && (
                      <span className="text-red-500 ml-1">
                        (Apenas para Admins)
                      </span>
                    )}
                  </label>
                  <p
                    className={`${
                      isDisabled ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {cargo.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        {errors.cargos && (
          <p className="mt-1 text-sm text-red-600">{errors.cargos}</p>
        )}

        {isGestorSelecionado && (
          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Gestor selecionado:</strong> Este usuário terá
              automaticamente acesso a todas as funcionalidades (Financeiro,
              Cadastramento e Vendas).
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-6">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-[#29306a] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#32bef0]"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
              Salvando...
            </>
          ) : isEditing ? (
            "Atualizar Funcionário"
          ) : (
            "Adicionar Funcionário"
          )}
        </button>
      </div>
    </form>
  );
};
